import { createTheme, PaletteColor, SimplePaletteColorOptions } from '@mui/material';
import { blue } from '@mui/material/colors';
import { TypographyOptions } from '@mui/material/styles/createTypography';

export interface AppPaletteColor extends PaletteColor {
  secondary?: string;
  darker?: string;
  darkest?: string;
  lighter?: string;
  lightest?: string;
}

export interface AppPaletteColorOptions extends SimplePaletteColorOptions {
  secondary?: string;
  darker?: string;
  darkest?: string;
  lighter?: string;
  lightest?: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    appGlass?: AppPaletteColor;
    appBlue?: AppPaletteColor;
    appGray?: AppPaletteColor;
  }
  interface PaletteOptions {
    appGlass?: AppPaletteColorOptions;
    appBlue?: AppPaletteColorOptions;
    appGray?: AppPaletteColorOptions;
    appRed?: AppPaletteColorOptions;
  }
}

const typographyStyleOverrides: TypographyOptions = {
  allVariants: {
    fontFamily: 'inherit',
  },
  h4: {
    fontSize: '1.375rem',
  },
  h5: {
    fontSize: '1.125rem',
  },
  h6: {
    fontSize: '1rem',
  },
  body1: {
    fontSize: '0.875rem',
  },
  body2: {
    fontSize: '0.8125rem',
  },
  subtitle2: {
    fontSize: '0.75rem',
  },
  button: {
    textTransform: 'none',
  },
};

const componentStyleOverrides = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        fontFamily: 'Montserrat, Verdana, Serif',
        wordWrap: 'break-word',
        overflowX: 'hidden',
      },
    },
  },
  MuiAppBar: {
    defaultProps: {
      elevation: 0,
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        fontFamily: 'inherit',
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      title: {
        fontWeight: 600,
      },
      action: {
        margin: 0,
        fontWeight: 500,
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        overflow: 'scroll',
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-root': {
          fontWeight: 600,
        },
      },
    },
  },
  MuiTableBody: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-root': {
          fontWeight: 500,
        },
      },
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      toolbar: {
        backgroundColor: 'transparent',
      },
      selectLabel: {
        fontWeight: 500,
      },
      displayedRows: {
        fontWeight: 500,
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        fontWeight: 500,
      },
    },
  },
  MuiContainer: {
    styleOverrides: {
      root: {
        '@media (max-width: 600px)': {
          paddingLeft: '8px !important',
          paddingRight: '8px !important',
        },
      },
    },
  },
};

export let darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: blue.A400,
    },
    appGlass: {
      main: 'rgba(255, 255, 255, 0.16) !important',
    },
    appBlue: {
      lightest: '#4fc3f7',
      light: '#2979ff',
      main: '#101F33',
      dark: '#081627',
      contrastText: '#4fc3f7',
    },
    appGray: {
      darkest: '#101010',
      darker: '#303030',
      dark: '#505050',
      main: '#707070',
      secondary: '#909090',
      light: '#B0B0B0',
      lighter: '#D0D0D0',
      lightest: '#F0F0F0',
      contrastText: '#F0F0F0',
    },
    appRed: {
      darkest: '#b71c1c',
      darker: '#d32f2f',
      dark: '#e53935',
      main: '#f44336',
      secondary: '#ef5350',
      light: '#e57373',
      lighter: '#ef9a9a',
      lightest: '#ffcdd2',
      contrastText: '#ffebee',
    },
    text: {
      secondary: '#ffffff',
    },
  },
  typography: {
    ...typographyStyleOverrides,
  },
});

darkTheme = {
  ...darkTheme,
  components: {
    ...componentStyleOverrides,
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          ...componentStyleOverrides.MuiCssBaseline.styleOverrides.body,
          backgroundColor: darkTheme.palette.appGray.dark,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: darkTheme.palette.appBlue.main,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: darkTheme.palette.appBlue.dark,
          borderWidth: 0,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        ...componentStyleOverrides.MuiCardHeader.styleOverrides,
        root: {
          backgroundColor: darkTheme.palette.appGray.darkest,
          color: darkTheme.palette.appBlue.lightest,
          borderBottom: '1px solid',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: darkTheme.palette.appBlue.lightest,
          },
        },
      },
    },
  },
};

export let lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: blue.A400,
      dark: '#fdfdfe',
      light: '#f9fbfc',
    },
    background: {
      default: '#e8e8e8',
    },
    appBlue: {
      lightest: '#4fc3f7',
      light: '#2979ff',
      main: '#101F33',
      dark: '#081627',
      contrastText: '#2979ff',
    },
    appGray: {
      darkest: '#101010',
      darker: '#303030',
      dark: '#505050',
      main: '#707070',
      secondary: '#F1F2F3',
      light: '#B0B0B0',
      lighter: '#D0D0D0',
      lightest: '#F0F0F0',
      contrastText: '#101010',
    },
    appRed: {
      darkest: '#b71c1c',
      darker: '#d32f2f',
      dark: '#e53935',
      main: '#f44336',
      secondary: '#ef5350',
      light: '#e57373',
      lighter: '#ef9a9a',
      lightest: '#ffcdd2',
      contrastText: '#ffebee',
    },
    text: {
      primary: '#1c2025',
      secondary: '#6c757d',
    },
  },
  typography: {
    ...typographyStyleOverrides,
  },
});

lightTheme = {
  ...lightTheme,
  components: {
    ...componentStyleOverrides,
    MuiCardHeader: {
      styleOverrides: {
        ...componentStyleOverrides.MuiCardHeader.styleOverrides,
        root: {
          backgroundColor: lightTheme.palette.primary.dark,
          color: lightTheme.palette.appBlue.light,
          borderBottom: '1px solid #e5eaf2',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#E0EFFF',
            color: '#2979ff !important',
            '&:hover': {
              backgroundColor: '#E0EFFF',
              color: '#2979ff !important',
            },
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: lightTheme.palette.text.primary,
          '&.Mui-selected': {
            backgroundColor: '#E0EFFF',
            color: '#2979ff !important',
          },
          borderColor: '#d9d9d9 !important',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          ...componentStyleOverrides.MuiTableHead.styleOverrides.root,
          backgroundColor: lightTheme.palette.primary.dark,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: lightTheme.palette.appBlue.light,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          '&.MuiButton-containedPrimary': {
            backgroundColor: lightTheme.palette.appBlue.light,
            color: '#ffffff',
          },
          '&.MuiButton-outlinedPrimary': {
            borderColor: 'rgba(41, 121, 255, 0.5)',
            color: lightTheme.palette.appBlue.light,
          },
        },
      },
    },
    MuiAppBar: {
      ...componentStyleOverrides.MuiAppBar,
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: lightTheme.palette.text.primary,
        },
      },
    },
  },
};
